import { Link } from 'react-router-dom';
import { ChevronRight, CheckCircle2 } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { buildOnboardingFromUser, isIdentityVerified } from '../../utils/onboardingFromUser';
import { Card } from '../new-components/ui/card';
import { TENANT } from '../../config';
import { SETTINGS_GROUPS, itemsByGroup, sectionsNeedingAttention } from './settings.registry';

/**
 * Settings landing — account summary, a "next step" callout, and the sections grouped under
 * headers (Account / Payment methods / Security / Documents). Each row links to its detail
 * route (/portal/settings/:key); rows whose onboarding step is still incomplete get an "action
 * needed" hint. The list/detail split replaces the old sidebar so the page works the same on
 * mobile and desktop.
 */
export function SettingsList() {
  const { user } = useUser();

  // Onboarding is derived per InvestorPortal's rule: only for non-admin users.
  const onboarding = user && user.role !== 'admin' ? buildOnboardingFromUser(user) : null;
  const active = !!onboarding && onboarding.shouldShowOnboardingBanner;
  const needsAttention = active ? sectionsNeedingAttention(onboarding!.completedSteps) : new Set<string>();
  // Once identity is verified the KYC row is hidden (it's terminal). Surface the status instead as a
  // read-only, non-navigable row at the end of the Account group so the section still reflects it.
  const identityVerified = isIdentityVerified(user);
  // The Accreditation row stays navigable, but show a verified check at a glance when it's done
  // (parity with the old sidebar's accreditation checkmark).
  const accreditationVerified = user?.accreditation_status === 'verified';

  return (
    <div>
      <div className="border-b border-border pb-4 mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl">Account Settings</h1>
      </div>

      {/* Sections, grouped */}
      {SETTINGS_GROUPS.map((group) => {
        // Drop sections hidden for this user (e.g. KYC once identity is verified).
        const items = itemsByGroup(group).filter((item) => !item.isHidden?.(user));
        if (!items.length) return null;
        return (
          <div key={group} className="mb-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1">
              {group}
            </h2>
            <Card className="border-border overflow-hidden divide-y divide-border gap-0">
              {items.map((item) => (
                <Link
                  key={item.key}
                  to={`/portal/settings/${item.key}`}
                  className="group flex items-center gap-4 px-4 py-4 transition-colors md:hover:bg-muted"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                  {needsAttention.has(item.key) && (
                    <span className="flex-shrink-0 text-[11px] font-semibold text-foreground bg-secondary border border-border px-2 py-0.5 rounded-full">
                      Action needed
                    </span>
                  )}
                  {item.key === 'accreditation' && accreditationVerified && (
                    <span className="flex flex-shrink-0 items-center gap-1.5 text-sm font-medium text-[hsl(var(--success))]">
                      <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                      Verified
                    </span>
                  )}
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform md:group-hover:translate-x-0.5" />
                </Link>
              ))}
              {group === 'Account' && TENANT.features.investing && identityVerified && (
                <div className="flex items-center gap-4 px-4 py-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground">Identity Verification</p>
                    <p className="text-sm text-muted-foreground">Your identity is verified</p>
                  </div>
                  <span className="flex flex-shrink-0 items-center gap-1.5 text-sm font-medium text-[hsl(var(--success))]">
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    Verified
                  </span>
                </div>
              )}
            </Card>
          </div>
        );
      })}
    </div>
  );
}

export default SettingsList;
